import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuthStore } from './store/authStore'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import { useEffect, useState } from 'react'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'

// Main App Pages
import POSPage from './pages/POSPage'
import TouchPOSPage from './pages/TouchPOSPage'
import MainLayout from './layouts/MainLayout'
import ManagementPage from './pages/ManagementPage'
import SettingsPage from './pages/SettingsPage'
import OrdersPage from './pages/OrdersPage'
import ReportsPage from './pages/ReportsPage'
import BusinessDayPage from './pages/BusinessDayPage'
import CustomersPage from './pages/CustomersPage'
import InventoryPage from './pages/InventoryPage'


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore(state => state.user)

  return user ? <>{children}</> : <Navigate to="/login" replace />
}

function AppContent() {
  const [isLoading, setIsLoading] = useState(true)
  const [connectionError, setConnectionError] = useState(false)
  const checkAuth = useAuthStore(state => state.checkAuth)

  useEffect(() => {
    const initAuth = async () => {
      try {
        await checkAuth()
        // Success - clear any previous errors
        setConnectionError(false)
      } catch (error: any) {
        console.error('Auth check failed:', error)
        
        // CRITICAL: Distinguish network errors from auth failures
        // Network errors (CORS, DNS, timeout) = connection error
        // Auth failures (401, 403, invalid token) = just redirect to login
        const isNetworkError = !error.response || error.code === 'ERR_NETWORK'
        
        if (isNetworkError) {
          console.warn('⚠️ Network connectivity issue detected')
          setConnectionError(true)
        } else {
          // Auth failure - this is normal, user just needs to login
          console.log('ℹ️ User not authenticated, will show login page')
          setConnectionError(false)
        }
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()
  }, [checkAuth])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading...</p>
        </div>
      </div>
    )
  }

  if (connectionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 rounded-full">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="mt-4 text-xl font-semibold text-center text-gray-900">
            Connection Error
          </h1>
          <p className="mt-2 text-sm text-center text-gray-600">
            Unable to connect to the server. Please ensure the backend is running.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-900 min-h-screen">
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Toaster position="bottom-left" />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/app" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/app/pos" replace />} />
            <Route path="pos" element={<POSPage />} />
            <Route path="touch-pos" element={<TouchPOSPage />} />
            <Route path="management" element={<ManagementPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="business-day" element={<BusinessDayPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="inventory" element={<InventoryPage />} />
          </Route>

        </Routes>
      </BrowserRouter>
    </div>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  )
}